/**
 * The merge wizard's shared data bag and the pure rules over it.
 *
 * Split out from `use-merge-wizard-modal` so it can be unit tested: that module
 * imports `@farmshare/ui-components`, which pulls in the ESM-only `color`
 * package that this Jest config can't transform. Everything here is data and
 * predicates, with type-only imports, so it stays loadable on its own.
 */

import type { MergeReviewPreview } from '../components/merge-wizard-review-step';
import type { SelectOption } from '../components/merge-wizard-select-step';
import {
  MERGE_FIELDS,
  type MergeFieldKey,
  type WizardCandidate,
} from '../components/merge-wizard-types';

export const MERGE_WIZARD_STEPS = ['Select', 'Compare', 'Review'];

/**
 * Everything the three steps share.
 *
 * This is the multi-step modal's `data` bag rather than `useState` inside a
 * wizard hook, because the renderer owns step navigation and asks
 * `canGoNext(data)` whether the footer button should be live. Query results
 * (`candidates`, `preview`) are published back into it by the step that loads
 * them for exactly that reason — the gate for "Next" on Compare is that the
 * candidate query has landed, and the gate for "Merge" is that the preview has.
 */
export interface MergeWizardData {
  selected: SelectOption[];
  masterId: string;
  fieldSources: Record<MergeFieldKey, string>;
  showAll: boolean;
  consent: boolean;
  candidates: WizardCandidate[];
  candidatesLoading: boolean;
  preview: MergeReviewPreview | null;
  previewLoading: boolean;
}

/**
 * Field-source map with every field pointing at one record.
 * @param masterId - The record every field should default to.
 * @returns The map.
 */
export function emptySources(masterId: string): Record<MergeFieldKey, string> {
  return MERGE_FIELDS.reduce(
    (acc, f) => ({ ...acc, [f.key]: masterId }),
    {} as Record<MergeFieldKey, string>,
  );
}

/**
 * Ids of the records currently selected for the merge.
 * @param data - Current wizard data.
 * @returns The selected ids.
 */
export const selectedIdsOf = (data: MergeWizardData) =>
  data.selected.map((s) => s._id);

/**
 * Whether two selections name the same set of records, in any order.
 * @param a - One list of ids.
 * @param b - The other.
 * @returns True when both hold exactly the same ids.
 */
function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * The patch to apply when the user edits the selection on the Select step.
 *
 * Everything chosen on the later steps was chosen ABOUT a particular set of
 * records: which one survives, whose value each field keeps, and consent to
 * retire a login. Carrying those across a change of set is how the wizard came
 * to merge into a record the user had just removed — go Back, swap the primary
 * out for someone else, and the old `masterId` still passed every gate. So any
 * change to the set drops them all, along with the query results that described
 * the old set, and Compare starts again from its defaults.
 *
 * Reordering the same records keeps everything; nothing was decided about order.
 * @param data - Current wizard data.
 * @param next - The new selection.
 * @returns The fields to merge into the wizard data.
 */
export function selectionPatch(
  data: MergeWizardData,
  next: SelectOption[],
): Partial<MergeWizardData> {
  if (
    sameIds(
      selectedIdsOf(data),
      next.map((s) => s._id),
    )
  ) {
    return { selected: next };
  }
  return {
    selected: next,
    masterId: '',
    fieldSources: emptySources(''),
    consent: false,
    candidates: [],
    candidatesLoading: false,
    preview: null,
    previewLoading: false,
  };
}

/**
 * The patch to apply when the surviving record changes.
 *
 * Field choices default back to the new primary, and both the consent and the
 * preview go: they were given for, and computed against, a different set of
 * records being retired.
 * @param masterId - The new primary record.
 * @returns The fields to merge into the wizard data.
 */
export function masterPatch(masterId: string): Partial<MergeWizardData> {
  return {
    masterId,
    fieldSources: emptySources(masterId),
    consent: false,
    preview: null,
  };
}

/**
 * Whether the primary and every per-field source are records in the current
 * selection.
 *
 * `selectionPatch` is what keeps this true; this is the second lock on the same
 * door. A merge is irreversible, so the gates and the submit path each check it
 * for themselves rather than trusting that no path leaves a stale id behind.
 * @param data - Current wizard data.
 * @returns True when nothing the merge would act on is outside the selection.
 */
export function isSelectionConsistent(data: MergeWizardData): boolean {
  const ids = selectedIdsOf(data);
  if (!data.masterId || !ids.includes(data.masterId)) return false;
  return Object.values(data.fieldSources).every(
    (sourceId) => !sourceId || ids.includes(sourceId),
  );
}

/**
 * The candidate query has landed for exactly the selected records — not merely
 * the same number of them, which a result for the previous selection also is.
 * @param data - Current wizard data.
 * @returns True when the loaded candidates are the selected records.
 */
function candidatesMatchSelection(data: MergeWizardData): boolean {
  return (
    !data.candidatesLoading &&
    sameIds(
      data.candidates.map((c) => c._id),
      selectedIdsOf(data),
    )
  );
}

export interface MergeVariables {
  winnerId: string;
  loserIds: string[];
  fieldSelections: { field: MergeFieldKey; sourceId: string }[];
}

/**
 * The merge mutation's variables for the current wizard data.
 *
 * Throws rather than returning something best-effort when the data doesn't
 * describe a merge within the selection: the multi-step renderer reports a
 * throw from `onComplete` and leaves the wizard open, which is the right
 * outcome for a request that must not be sent.
 * @param data - Current wizard data.
 * @returns The winner, the records to retire, and the fields they contribute.
 */
export function mergeVariablesOf(data: MergeWizardData): MergeVariables {
  if (!mergeWizardGates.select(data) || !isSelectionConsistent(data)) {
    throw new Error(
      'The primary record is no longer one of the selected customers. Go back and choose it again.',
    );
  }
  return {
    winnerId: data.masterId,
    loserIds: selectedIdsOf(data).filter((id) => id !== data.masterId),
    fieldSelections: MERGE_FIELDS.filter(
      (f) =>
        data.fieldSources[f.key] && data.fieldSources[f.key] !== data.masterId,
    ).map((f) => ({ field: f.key, sourceId: data.fieldSources[f.key] })),
  };
}

/**
 * Retiring a record that has a login disables that login — require consent.
 * @param data - Current wizard data.
 * @returns Whether the consent checkbox must be ticked before merging.
 */
export function requiresConsent(data: MergeWizardData): boolean {
  return data.candidates.some((c) => c._id !== data.masterId && c.hasLogin);
}

/**
 * Records the merge would retire that can't be retired from this processor,
 * because they also do business at another one.
 * @param data - Current wizard data.
 * @returns The blocked records other than the primary.
 */
export function blockedRetirees(data: MergeWizardData): WizardCandidate[] {
  return data.candidates.filter(
    (c) => c._id !== data.masterId && c.retireBlockedReason,
  );
}

/**
 * Per-step "can the footer's Next/Merge button fire" rules.
 *
 * The renderer calls `canGoNext(data)` on every render, so each of these is the
 * only thing standing between a half-loaded step and a merge.
 */
export const mergeWizardGates = {
  /**
   * Select: between two and three records, inclusive.
   * @param data
   */
  select: (data: MergeWizardData) =>
    data.selected.length >= 2 && data.selected.length <= 3,
  /**
   * Compare: the candidate query landed for exactly the selected records, and
   * the primary (and every field source) is one of them.
   * @param data
   */
  compare: (data: MergeWizardData) =>
    candidatesMatchSelection(data) &&
    isSelectionConsistent(data) &&
    blockedRetirees(data).length === 0,
  /**
   * Review: everything Compare requires still holds, a preview came back within
   * the cap, and any required consent is given.
   * @param data
   */
  review: (data: MergeWizardData) =>
    candidatesMatchSelection(data) &&
    isSelectionConsistent(data) &&
    blockedRetirees(data).length === 0 &&
    !data.previewLoading &&
    !!data.preview &&
    !data.preview.exceedsCap &&
    (!requiresConsent(data) || data.consent),
};
